import { Box, Card, Checkbox, Chip, Dialog, FormControl, InputLabel, MenuItem, Button as MUIButton, Select, Tooltip, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import dayjs from 'dayjs';
import { ReactElement, useState } from 'react';
import {
  BulkDeleteButton,
  CreateButton,
  Datagrid,
  EditButton,
  Filter,
  FunctionField,
  List,
  TextField,
  TopToolbar,
  useListController,
  useTranslate,
  DeleteButton,
  SelectField,
  WithPermissions,
  Button
} from 'react-admin';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionButton } from 'src/routes/components/permission-button';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddressField from 'src/pages/admin/components/AddressField';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Resources } from '../../resource';
import { Iconify } from 'src/components/iconify';
import CreateSearchPlaceholder from './Create';
import EditSearchPlaceholder from './Edit';
import { chains } from 'src/assets/data';
import AuditLogDialog from 'src/pages/admin/components/AuditLog/AuditLogDialog';


const ListFilter = (props: any) => {
  const { filterValues } = useListController();

  return (
    <Filter {...(props || {})}>
      {/* <FilterGroup alwaysOn spacing={0} p={0}>
        <FilterGroup>
          <XTextInput source="symbol" label="module.tokenMetadata.symbol" />
        </FilterGroup>
      </FilterGroup> */}
    </Filter>
  );
};

const View = () => {
  const [popup, setPopup] = useState<ReactElement>();
  const translate = useTranslate();
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditTitle, setAuditTitle] = useState('Audit Logs');
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  return (
    <PermissionGuard resource="search-placeholders" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('resource.searchPlaceholder')}
          links={[
            { name: translate('resource.configs'), href: '#' },
            {
              name: translate('resource.searchPlaceholder'),
              href: `/${Resources.searchPlaceholder}`,
            },
            { name: translate('resource.list') },
          ]}
          action={
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
              mb={1}
            >
              <PermissionGuard resource="search-placeholders" action="create" fallback={<></>}>
                <CreateButton
                  variant="contained"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    setPopup(<CreateSearchPlaceholder onClose={() => setPopup(undefined)} />);
                  }}
                />
              </PermissionGuard>
              <PermissionGuard resource="audit-logs" action="read" fallback={<></>}>
                <Button
                  variant="contained"
                  size="medium"
                  color="secondary"
                  onClick={() => {
                    setSelectedRecord({});
                    setAuditTitle(`${translate('resource.auditLogs')} - ${translate('resource.searchPlaceholder')}`);
                    setAuditLogOpen(true);
                  }}
                  label={translate('resource.auditLogs')}
                >
                  <Iconify icon="ph:note" />
                </Button>
              </PermissionGuard>
            </Stack>
          }
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            filters={<ListFilter />}
            perPage={10}
            exporter={false}
            actions={false}
            empty={false}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={
                  <PermissionButton resource="search-placeholders" action="delete">
                    <BulkDeleteButton />
                  </PermissionButton>
                }
                sx={{
                  '.RaBulkActionsToolbar-toolbar': {
                    position: 'relative !important',
                    transform: 'none !important',
                  },
                }}
              >
                <FunctionField
                  label="module.searchPlaceholder.placeholder"
                  textAlign="left"
                  minWidth={250}
                  sortable={false}
                  render={(record: any) => {
                    const { placeholder } = record;
                    if (!placeholder) return '';

                    const jsonString = JSON.stringify(placeholder, null, 2);

                    return (
                      <Tooltip title={jsonString} arrow>
                        <Typography
                          sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: '0.875rem',
                          }}
                        >
                          {jsonString}
                        </Typography>
                      </Tooltip>
                    );
                  }}
                />
                <FunctionField
                  label="module.searchPlaceholder.contractAddress"
                  render={(record: any) => <AddressField address={record.contractAddress} />}
                  sortable={false}
                />

                <FunctionField
                  label="module.searchPlaceholder.enable"
                  render={(record) => (
                    <Chip
                      label={record.enable ? 'True' : 'False'}
                      color={record.enable ? 'success' : 'error'}
                      size="small"
                    />
                  )}
                  sortable={false}
                />

                <FunctionField
                  render={(record: any) => (
                    <Stack direction="row" spacing={2}>
                      <PermissionGuard
                        resource="search-placeholders"
                        action="update"
                        fallback={<></>}
                      >
                        <EditButton
                          onClick={(e) => {
                            e.preventDefault();
                            setPopup(
                              <EditSearchPlaceholder
                                id={record.id}
                                onClose={() => setPopup(undefined)}
                              />
                            );
                          }}
                        />
                      </PermissionGuard>
                      <PermissionGuard
                        resource="search-placeholders"
                        action="delete"
                        fallback={<></>}
                      >
                        <DeleteButton mutationMode="pessimistic" />
                      </PermissionGuard>
                    </Stack>
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
        <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="md" fullWidth>
          {popup}
        </Dialog>
        <AuditLogDialog
          open={auditLogOpen}
          onClose={() => setAuditLogOpen(false)}
          recordId={selectedRecord?.id}
          resource={Resources.searchPlaceholder}
          title={auditTitle}
        />
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
